<template>
  <FlashNotifications ref="flash" />

  <!-- ═══════════════ MOBILE ═══════════════ -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] min-h-screen">

    <!-- Mobile skeleton -->
    <template v-if="!useSettingsStoreObj().init_data_loaded">
      <div class="animate-pulse pb-28">
        <!-- Logo placeholder -->
        <div class="flex flex-col items-center pt-8 pb-6 px-4">
          <div class="w-full h-20 bg-gray-200 dark:bg-gray-700 rounded-2xl"></div>
          <div class="mt-2 h-2.5 w-36 bg-gray-100 dark:bg-gray-800 rounded mx-auto"></div>
          <div class="mt-3 h-7 w-28 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
        </div>
        <div class="h-2.5 w-16 bg-gray-200 dark:bg-gray-700 rounded mx-4 mb-2"></div>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
          <div class="px-4 py-3.5 flex items-center justify-between gap-4">
            <div class="flex-1 space-y-1.5">
              <div class="h-3 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div class="h-2.5 w-48 bg-gray-100 dark:bg-gray-800 rounded"></div>
            </div>
            <div class="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0"></div>
          </div>
        </div>
        <div class="h-2.5 w-16 bg-gray-200 dark:bg-gray-700 rounded mx-4 mt-5 mb-2"></div>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
          <div class="px-4 py-3 space-y-1.5">
            <div class="h-2.5 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div class="h-8 w-full bg-gray-100 dark:bg-gray-800 rounded"></div>
          </div>
        </div>
      </div>
    </template>

    <!-- Mobile loaded content -->
    <template v-else>
      <div class="pb-28">
        <!-- Logo upload area -->
        <div class="flex flex-col items-center pt-8 pb-2 px-4">
          <button type="button"
                  @click="toggleModal"
                  @mouseenter="showCameraIcon = true"
                  @mouseleave="showCameraIcon = false"
                  class="relative w-full h-20 rounded-2xl overflow-hidden border-2 border-dashed border-gray-200 dark:border-gray-700">
            <div v-if="!useSettingsStoreObj().current_user.branding.logo_url"
                 class="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-gray-400 dark:text-gray-600">
              <svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              <span class="text-xs font-medium">Upload logo</span>
            </div>
            <img v-else :src="useSettingsStoreObj().current_user.branding.logo_url" alt="Brand logo"
                 class="absolute inset-0 w-full h-full object-contain p-3" />
            <div v-if="showCameraIcon" class="absolute inset-0 bg-gray-900/40 flex items-center justify-center">
              <svg class="w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
            </div>
          </button>
          <p class="mt-2 text-xs text-gray-400 dark:text-gray-500">Recommended: 300 x 100 px</p>
          <button type="button" @click="toggleModal"
                  class="mt-3 px-5 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-full">
            Change Logo
          </button>
        </div>

        <!-- Visibility -->
        <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Visibility</p>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
          <div class="px-4 py-3.5 flex items-center justify-between gap-4">
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 dark:text-gray-100">Enable branding</p>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-snug">{{ I18N.website_content.account_settings.enable_branding_description }}</p>
            </div>
            <button type="button"
                    @click="updateBrandingPageData({target: {name: 'user[enable_branding]', type: 'checkbox', checked: !useSettingsStoreObj().current_user.enable_branding}})"
                    :class="useSettingsStoreObj().current_user.enable_branding ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'"
                    class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 focus:outline-none">
              <span :class="useSettingsStoreObj().current_user.enable_branding ? 'translate-x-5' : 'translate-x-0.5'"
                    class="inline-block h-5 w-5 mt-0.5 transform rounded-full bg-white shadow transition-transform duration-200"/>
            </button>
          </div>
        </div>

        <!-- Website -->
        <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Website</p>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
          <div class="px-4 py-3">
            <label class="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Website URL</label>
            <input type="url"
                   v-model="branding.website_url"
                   @focusout="updateBrandingPageData"
                   name="branding[website_url]"
                   placeholder="https://yourwebsite.com"
                   class="w-full text-sm text-gray-900 dark:text-gray-100 bg-transparent focus:outline-none placeholder-gray-300 dark:placeholder-gray-600" />
          </div>
        </div>
      </div>
    </template>
  </div>

  <!-- ═══════════════ DESKTOP ═══════════════ -->
  <div class="hidden sm:block min-h-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- Desktop skeleton -->
    <template v-if="!useSettingsStoreObj().init_data_loaded">
      <div class="animate-pulse">
        <div class="sticky top-14 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
          <div class="max-w-5xl mx-auto px-6 flex items-center justify-between h-14">
            <div class="flex items-center gap-2.5">
              <div class="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
              <div class="space-y-1.5">
                <div class="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-2.5 w-24 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="max-w-5xl mx-auto px-6 py-6">
        <div class="flex flex-col lg:flex-row gap-6">
          <div class="flex-1 space-y-4 min-w-0">
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
              <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
                <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800"></div>
                <div class="h-3.5 w-28 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
              <div class="px-6 py-4 flex items-center justify-between">
                <div class="space-y-1.5 flex-1 mr-4">
                  <div class="h-3.5 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div class="h-3 w-56 bg-gray-100 dark:bg-gray-800 rounded"></div>
                </div>
                <div class="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0"></div>
              </div>
            </div>
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
              <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
                <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800"></div>
                <div class="h-3.5 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
              <div class="px-6 py-4 space-y-2">
                <div class="h-2.5 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-10 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
              </div>
            </div>
          </div>
          <div class="lg:w-64 flex-shrink-0">
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
              <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
                <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800"></div>
                <div class="h-3.5 w-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
              <div class="px-6 py-5 space-y-3">
                <div class="h-24 w-full bg-gray-100 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700"></div>
                <div class="h-2.5 w-32 bg-gray-100 dark:bg-gray-800 rounded mx-auto"></div>
                <div class="h-8 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </template>

    <!-- Desktop loaded content -->
    <template v-else>
      <!-- Sub-page header -->
      <div class="sticky top-14 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
        <div class="max-w-5xl mx-auto px-6 flex items-center justify-between h-14">
          <div class="flex items-center gap-2.5">
            <div class="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
              <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 20h9"/>
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
              </svg>
            </div>
            <div>
              <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Branding</h1>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Public Profile</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="max-w-5xl mx-auto px-6 py-6">
        <div class="flex flex-col lg:flex-row gap-6">

          <!-- Left column -->
          <div class="flex-1 space-y-4 min-w-0">

            <!-- Enable Branding card -->
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
              <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
                <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </div>
                <h2 class="text-sm font-semibold text-gray-900 dark:text-white">Visibility</h2>
              </div>
              <div class="px-6 py-4 flex items-center justify-between gap-6">
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-800 dark:text-gray-200">Enable branding</p>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-relaxed">{{ I18N.website_content.account_settings.enable_branding_description }}</p>
                </div>
                <button type="button"
                        @click="updateBrandingPageData({target: {name: 'user[enable_branding]', type: 'checkbox', checked: !useSettingsStoreObj().current_user.enable_branding}})"
                        :class="useSettingsStoreObj().current_user.enable_branding ? 'bg-blue-600 shadow-sm shadow-blue-500/30' : 'bg-gray-200 dark:bg-gray-700'"
                        class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-[#1C2128]">
                  <span :class="useSettingsStoreObj().current_user.enable_branding ? 'translate-x-5' : 'translate-x-0'"
                        class="inline-block h-5 w-5 mt-0.5 ml-0.5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"></span>
                </button>
              </div>
            </div>

            <!-- Website URL card -->
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
              <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
                <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                  </svg>
                </div>
                <h2 class="text-sm font-semibold text-gray-900 dark:text-white">Website</h2>
              </div>
              <div class="px-6 py-4">
                <label class="block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">Website URL</label>
                <input type="url"
                       v-model="branding.website_url"
                       @focusout="updateBrandingPageData"
                       name="branding[website_url]"
                       placeholder="https://yourwebsite.com"
                       class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
                <p class="mt-1.5 text-xs text-gray-400 dark:text-gray-500">Shown alongside your branding logo on your public profile.</p>
              </div>
            </div>

          </div>

          <!-- Right column: Logo card -->
          <div class="lg:w-64 flex-shrink-0">
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
                <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                </div>
                <h2 class="text-sm font-semibold text-gray-900 dark:text-white">Logo</h2>
              </div>
              <div class="px-6 py-5 space-y-4">
                <button type="button"
                        @click="toggleModal"
                        @mouseenter="showCameraIcon = true"
                        @mouseleave="showCameraIcon = false"
                        class="relative w-full h-24 rounded-lg overflow-hidden border-2 border-dashed border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 transition-colors">
                  <div v-if="!useSettingsStoreObj().current_user.branding.logo_url"
                       class="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-gray-400 dark:text-gray-600">
                    <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                    <span class="text-xs font-medium">Upload logo</span>
                  </div>
                  <img v-else :src="useSettingsStoreObj().current_user.branding.logo_url" alt="Brand logo"
                       class="absolute inset-0 w-full h-full object-contain p-3" />
                  <div v-if="showCameraIcon" class="absolute inset-0 bg-gray-900/40 flex items-center justify-center">
                    <svg class="w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                      <circle cx="12" cy="13" r="4"/>
                    </svg>
                  </div>
                </button>
                <p class="text-xs text-center text-gray-400 dark:text-gray-500">Recommended: 300 x 100 px</p>
                <button type="button" @click="toggleModal"
                        class="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors">
                  Change logo
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </template>
  </div>

  <!-- ═══════════════ SHARED MODAL ═══════════════ -->
  <div v-show="showModal" class="fixed inset-0 z-40 bg-gray-900/60 backdrop-blur-sm"></div>
  <div v-show="showModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
    <div class="relative w-full max-w-md pointer-events-auto">
      <div class="bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">

        <div class="px-6 pt-6 pb-4 border-b border-gray-100 dark:border-gray-700 flex items-start justify-between gap-4">
          <div>
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">{{ I18N.website_content.account_settings.upload_logo }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{{ I18N.website_content.account_settings.upload_logo_description }}</p>
          </div>
          <button @click="toggleModal" type="button"
                  class="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex-shrink-0">
            <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="px-6 py-5 space-y-4">
          <div class="rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 h-28 flex items-center justify-center overflow-hidden bg-gray-50 dark:bg-[#161b22]">
            <img v-if="logoPreview" :src="logoPreview" alt="Logo preview" class="max-w-full max-h-full object-contain p-3" />
            <div v-else class="flex flex-col items-center gap-1.5 text-gray-400 dark:text-gray-600">
              <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
              <span class="text-xs">No logo selected</span>
            </div>
          </div>
          <div class="flex gap-2">
            <button type="button" @click="openFilePicker"
                    class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              Choose file
            </button>
            <button v-if="useSettingsStoreObj().current_user.branding.logo_url"
                    type="button" @click="removeBranding"
                    class="flex items-center justify-center gap-2 px-4 py-2.5 border border-red-200 dark:border-red-800/50 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
              <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
              </svg>
              Remove
            </button>
          </div>
          <input type="file" ref="fileInput" class="hidden" accept=".png, .jpg, .jpeg" @change="selectImage" />
        </div>

        <div class="px-6 py-4 bg-gray-50 dark:bg-[#161b22] border-t border-gray-100 dark:border-gray-700 flex gap-3 justify-end">
          <button @click="toggleModal" type="button" ref="closeProfilePictureModal"
                  class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            Cancel
          </button>
          <button @click="uploadBrandingPic" type="button"
                  class="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2 min-w-[5rem] justify-center">
            <svg v-if="show_loading_in_upload_btn" class="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
            </svg>
            <span>{{ show_loading_in_upload_btn ? I18N.application.onboarding.saving : I18N.website_content.account_settings.personal.save }}</span>
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import Constants from "controllers/vue_components/mixins/constants"
import {useSettingsStore} from 'controllers/vue_components/settings/v1/store'
import RequestMixin from "controllers/vue_components/mixins/request"
import FlashNotifications from "controllers/vue_components/flash_toast"
import axios from 'redaxios';

export default {
    name: 'ProfileBranding',
    props: [],
    components: { FlashNotifications },
    mixins: [Constants, RequestMixin],
    data() {
        return {
            showCameraIcon: false,
            showModal: false,
            show_loading_in_upload_btn: false,
            logoPreview: null,
            selectedlogo: null,
            branding: {
                logo: null,
                website_url: ''
            }
        };
    },
    methods: {
        useSettingsStoreObj() {
            return useSettingsStore()
        },
        toggleModal(){
          this.showModal = !this.showModal;
          this.logoPreview = this.useSettingsStoreObj().current_user.branding.logo_url
        },
        selectImage(event) {
            const file = event.target.files[0];
            this.selectedlogo = file;
            if (file) {
                const reader = new FileReader();
                reader.onload = (e) => { this.logoPreview = e.target.result; };
                reader.readAsDataURL(file);
            }
        },
        async uploadBrandingPic() {
            const formData = new FormData()
            if (this.selectedlogo != null) {
                formData.append('branding[logo]', this.selectedlogo);
            }
            formData.append('branding[website_url]', this.branding.website_url);
            try {
                const response = await axios.put(`/api/v1/host/settings/brandings`, formData);
                if(this.showModal){ this.toggleModal(); }
                await this.useSettingsStoreObj().fetchCurrentUser();
                this.$refs.flash.show(response.data.message, 'notice');
            } catch (error) {
                console.error('Error uploading image:', error);
            }
        },
        async removeBranding(){
            this.$refs.fileInput.value = null;
            let res = await this.axiosRequest('DELETE', '/api/v1/host/settings/brandings', {}, false)
            if (res.js_error == false && res.response.success) {
                this.toggleModal();
                await this.useSettingsStoreObj().fetchCurrentUser();
                this.$refs.flash.show(res.response.message, 'notice');
            }
        },
        openFilePicker() {
            this.$refs.fileInput.click();
        },
        async updateBrandingPageData(e) {
            let [objectName, fieldName] = e.target.name.split('[').map(part => part.replace(']', ''));
            let fieldValue = ''
            if (e.target.type === 'checkbox') {
                fieldValue = e.target.checked
            } else {
                fieldValue = e.target.value
            }
            if (objectName == 'user' && this.useSettingsStoreObj().current_user[fieldName] !== fieldValue) {
                let response = await this.useSettingsStoreObj().updateCurrentUser({ user: { [fieldName]: fieldValue } })
                if (response.js_error == false) {
                    if (response.response.success) {
                        this.$refs.flash.show('Profile settings saved', 'notice');
                    } else {
                        this.$refs.flash.show(response.response.message, 'alert');
                    }
                } else {
                    this.$refs.flash.show(response.error.data.message, 'alert');
                }
            }
            if (objectName == 'branding') {
                this.branding[fieldName] = fieldValue
                this.uploadBrandingPic();
            }
        },
    },
    computed: {},
    created() {
        this.$watch(
            () => this.useSettingsStoreObj().init_data_loaded,
            (loaded) => {
                if (loaded) {
                    this.logoPreview = this.useSettingsStoreObj().current_user.branding?.logo_url
                    this.branding.website_url = this.useSettingsStoreObj().current_user.branding?.website_url
                }
            },
            { immediate: true }
        )
    }
};
</script>
